'use client';

import { useTranslation } from 'react-i18next';
import { PortalLink, useLocale, BlockIcon } from '@akademi/site-builder-runtime';
import { Button, Card, CardContent, CardHeader, CardTitle, Separator } from '@akademi/ui';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { textSecondary } from '../_shared/styles';
import { tx } from '../_shared/coerce';
import { formatMarketPrice, mpSurface, type MarketPrice } from './_shared';
import { useMarketplaceCart } from './_useMarketplaceCart';

type Localized = string | Record<string, string>;

export interface MarketplaceCartSummaryProps {
  title?: Localized;
  icon?: string;
  empty?: boolean;
  browseCatalogHref?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEMO_SUBTOTAL: MarketPrice = { amount: '1848.00', currency: 'TRY' };
const DEMO_DISCOUNT: MarketPrice = { amount: '150.00', currency: 'TRY' };
const DEMO_TOTAL: MarketPrice = { amount: '1698.00', currency: 'TRY' };

/**
 * Cart price-breakdown box — akademi-lms-v2's `ShoppingCartPage` "Cart
 * summary" card (Subtotal / Discount / Sale discount / Total). With a
 * {@link useMarketplaceCart} source mounted the figures come from the live
 * `/marketplace/cart`; with no source they're the authored props — the
 * "fallbacks stay, for testing" contract.
 */
export function MarketplaceCartSummary({
  title,
  icon = 'receipt',
  empty = false,
  browseCatalogHref = '/marketplace/catalog',
  paddingOuter,
  paddingInner,
}: MarketplaceCartSummaryProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const cart = useMarketplaceCart();

  const live = cart?.cart;
  // With a source mounted the live cart is the single truth (even while it's
  // still null/loading → treat as empty, never fall back to demo numbers).
  const isEmpty = cart ? (live?.items.length ?? 0) === 0 : empty;
  const subtotalV = cart ? live?.totalListPrice : DEMO_SUBTOTAL;
  const discountV = cart ? live?.totalDiscount : DEMO_DISCOUNT;
  const saleDiscountV = cart ? live?.subtotal : undefined;
  const totalV = cart ? live?.total : DEMO_TOTAL;

  const rows: { label: string; value: string | undefined }[] = [
    { label: t('marketplace.cartSummary.subtotal'), value: formatMarketPrice(subtotalV, locale) },
    { label: t('marketplace.cartSummary.discount'), value: formatMarketPrice(discountV, locale) },
    {
      label: t('marketplace.cartSummary.saleDiscount'),
      value: formatMarketPrice(saleDiscountV, locale),
    },
  ].filter((r) => r.value);

  const totalText = formatMarketPrice(totalV, locale) ?? t('marketplace.cartSummary.free');

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardHeader className="p-0">
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
            <BlockIcon value={icon} size={18} />
            {tx(title) || t('marketplace.cartSummary.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-3">
          {isEmpty ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3, 12px)',
                alignItems: 'flex-start',
              }}
            >
              <p style={{ ...textSecondary, margin: 0 }}>{t('marketplace.cartSummary.empty')}</p>
              <Button asChild variant="brand">
                <PortalLink href={browseCatalogHref}>
                  {t('marketplace.cartSummary.browseCatalog')}
                </PortalLink>
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2, 8px)' }}>
              {rows.map((row) => (
                <div
                  key={row.label}
                  style={{ display: 'flex', justifyContent: 'space-between', ...textSecondary }}
                >
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ))}

              <Separator />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-lg, 18px)',
                }}
              >
                <span>{t('marketplace.cartSummary.total')}</span>
                <span>{totalText}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </SectionShell>
  );
}
